import { getBrowserTabsAttachments } from "@mirage/mosaics/Chat/components/chat/BrowserTabs/BrowserTabsIngestion";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type { StackChatMetadata } from "@mirage/mosaics/ConversationAssistant/components/stack-chat/useStackChatDetails";
import type {
  ConversationMessage,
  DocumentInfo,
  WorkflowAgent,
} from "@mirage/service-conversation/types";
import {
  browserTabChatSourceToDocumentInfo,
  convertChatSourceToDocumentInfo,
  convertUUIDsToDocumentInfo,
} from "@mirage/service-conversation/utils/convertChatSourceToDocumentInfo";
import { useCallback } from "react";

/**
 * Returns the attachments we should pass to the conversations API message payload.
 * This should include attachments based on:
 * 1. the currently attached Stack;
 * 2. sources configured into a workflow agent;
 * 3. any additional sources manually added by the user;
 * 4. any relevant transient sources (the current transient source or transient sources in past messages)
 */
export function useGetConversationAttachments(
  stackChatDetails: StackChatMetadata | null,
  workflowAgent: WorkflowAgent | undefined,
  manuallyAddedSources: ChatSource[],
  getTransientSource: (() => Promise<DocumentInfo>) | undefined,
  conversationHistory: ConversationMessage[],
  browserContextTabIDs: Set<number> | undefined,
) {
  return useCallback(async () => {
    const allAttachments: DocumentInfo[] = [];
    if (stackChatDetails) {
      allAttachments.push(...stackChatDetails.stackSources);
    }
    if (workflowAgent) {
      allAttachments.push(
        ...(workflowAgent.accessType === "private"
          ? convertUUIDsToDocumentInfo(workflowAgent.sources)
          : (workflowAgent.sourceContents || []).map(
              convertChatSourceToDocumentInfo,
            )),
      );
    }
    allAttachments.push(
      ...manuallyAddedSources.map(convertChatSourceToDocumentInfo),
    );
    if (getTransientSource) {
      allAttachments.push(await getTransientSource());
    }

    // Include all referenced transient sources in the conversation history
    for (const message of conversationHistory) {
      if (message.messageType?.case === "userMessage") {
        for (const attachment of message.messageType.value.attachments) {
          if (attachment.transient) {
            allAttachments.push(attachment);
          }
        }
      }
    }

    if (browserContextTabIDs) {
      const browserTabsAttachments =
        await getBrowserTabsAttachments(browserContextTabIDs);
      const browserTabsDocumentInfos = browserTabsAttachments
        .map(browserTabChatSourceToDocumentInfo)
        .filter((info) => info !== undefined);
      allAttachments.push(...browserTabsDocumentInfos);
    }

    // Deduplicate attachments based on their uuid (first one wins)
    const deduplicatedAttachments: DocumentInfo[] = [];
    const attachmentUUIDs = new Set<string>();
    for (const attachment of allAttachments) {
      if (!attachmentUUIDs.has(attachment.uuid)) {
        deduplicatedAttachments.push(attachment);
        attachmentUUIDs.add(attachment.uuid);
      }
    }
    return deduplicatedAttachments;
  }, [
    stackChatDetails,
    workflowAgent,
    manuallyAddedSources,
    getTransientSource,
    browserContextTabIDs,
    conversationHistory,
  ]);
}
